import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronDown,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import { useEscritorios } from '../hooks/useEscritorios';
import { useSidebarRecolhido } from '../hooks/useSidebarRecolhido';
import { CromoAppContext, type CromoApp } from '../lib/cromoApp';
import { useSubscription } from '../hooks/useSubscription';
import { useTheme } from '../context/ThemeProvider';
import { useSyncStatus } from '../hooks/useSyncStatus';
import { SubscriptionGate } from './SubscriptionGate';
import { SeletorDeConta } from './delegacao/AtuacaoShell';
import { useAtuacao } from '../lib/delegacao/atuacao';
import { InstallBar } from './InstallBar';
import { FabProvider, FabRenderer } from './ui/Fab';
import { primeiroEUltimoNome } from '../lib/formatters';
import { cn } from '../lib/utils';
import { SyncIndicator } from './SyncIndicator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from './ui/sheet';
import { Spinner } from './ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Logo } from './ui';

export interface NavItem {
  label: string;
  icon: ReactNode;
  path: string;
  /** Rótulo de grupo — um separador com label é renderizado antes do primeiro item de cada grupo. */
  group?: string;
}

interface SidebarLayoutProps {
  navItems: NavItem[];
  headerExtra?: ReactNode;
  /** Faixa no topo do conteúdo (ex.: "Atuando na conta de…"). */
  banner?: ReactNode;
  footerLink?: { label: string; icon: ReactNode; path: string };
  /** Classe CSS extra aplicada ao sidebar (desktop aside, mobile header e sheet). */
  sidebarClassName?: string;
  /**
   * O conteúdo num card sobre o fundo — a moldura (docs/PLANO_MOLDURA.md). Só a
   * partir de 640px; no celular, o conteúdo segue de ponta a ponta.
   */
  emPainel?: boolean;
}

function NavButton({
  item,
  isActive,
  recolhido,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  recolhido?: boolean;
  onClick?: () => void;
}) {
  const link = (
    <Link
      to={item.path}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      aria-label={recolhido ? item.label : undefined}
      className={cn(
        'flex items-center rounded-lg text-sm transition-colors',
        recolhido ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2',
        isActive
          ? 'bg-[var(--color-primary)]/10 font-semibold text-[var(--color-primary)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-text-primary)]/5 hover:text-[var(--color-text-primary)]'
      )}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      {!recolhido && item.label}
    </Link>
  );

  // Recolhido só há o ícone: o rótulo passa a viver no tooltip, senão o menu
  // vira adivinhação — e o grupo a que o item pertence some junto.
  if (!recolhido) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">
        {item.group ? `${item.group} · ${item.label}` : item.label}
      </TooltipContent>
    </Tooltip>
  );
}

function UserSection({ recolhido, onNavClick }: { recolhido?: boolean; onNavClick?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const { data: escritorios } = useEscritorios();
  const { isDarkMode, toggleTheme } = useTheme();
  const atuacao = useAtuacao();
  const [loggingOut, setLoggingOut] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const handleLogout = () => {
    setLoggingOut(true);
    logout();
  };

  const isAdmin = user?.role === 'admin';
  const initials = user?.nome?.charAt(0).toUpperCase() ?? '?';

  // Info de assinatura (apenas não-admin)
  const [now] = useState(() => Date.now());
  useEffect(() => {}, []); // force re-render on mount for fresh Date.now()
  const { planLabel, showUpgrade } = useMemo(() => {
    if (isAdmin || subLoading) return { planLabel: '', showUpgrade: false };
    if (!subscription) return { planLabel: 'Sem plano', showUpgrade: true };
    if (subscription.cortesia) return { planLabel: 'Cortesia', showUpgrade: false };
    if (subscription.escritorio_que_paga) return { planLabel: 'Pelo escritório', showUpgrade: false };
    if (subscription.status === 'TRIAL' && subscription.trial_fim) {
      const daysLeft = Math.max(0, Math.ceil(
        (new Date(subscription.trial_fim).getTime() - now) / 86_400_000
      ));
      return { planLabel: `Trial · ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`, showUpgrade: true };
    }
    if (subscription.plano) {
      return { planLabel: `Plano ${subscription.plano.nome}`, showUpgrade: subscription.plano.grupo !== 'premium_ia' };
    }
    return { planLabel: 'Sem plano', showUpgrade: true };
  }, [isAdmin, subLoading, subscription, now]);

  const avatar = user?.picture_url && !avatarFailed ? (
    <img
      src={user.picture_url}
      alt=""
      aria-hidden="true"
      referrerPolicy="no-referrer"
      onError={() => setAvatarFailed(true)}
      className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
    />
  ) : (
    <div
      aria-hidden="true"
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-[0.85rem] font-semibold text-[var(--color-primary)]"
    >
      {initials}
    </div>
  );

  return (
    <div className={cn(recolhido ? 'p-2' : 'p-3')}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={recolhido ? `Conta de ${user?.nome ?? 'usuário'}` : undefined}
            className={cn(
              'flex w-full items-center rounded-lg text-left transition-colors hover:bg-[var(--color-text-primary)]/5',
              recolhido ? 'justify-center px-1 py-2' : 'gap-3 px-3 py-2.5',
            )}
          >
            {avatar}
            {!recolhido && (
              <>
                <div className="min-w-0 flex-1">
                  {/* Primeiro e último nome: o inteiro empurrava o menu para fora da tela
                      em monitor pequeno. O nome inteiro fica no title. */}
                  <p
                    className="break-words text-[0.8rem] font-semibold text-[var(--color-text-primary)]"
                    title={user?.nome ?? undefined}
                  >
                    {primeiroEUltimoNome(user?.nome)}
                  </p>
                  {!isAdmin && planLabel && (
                    <p className="break-words text-[11px] text-[var(--color-text-secondary)]">
                      {planLabel}
                    </p>
                  )}
                  {!isAdmin && escritorios && escritorios.length > 0 && (
                    <p className="break-words text-[11px] text-[var(--color-text-secondary)]">
                      <Building2 className="inline h-2.5 w-2.5 mr-0.5" />
                      {escritorios.length === 1
                        ? escritorios[0].nome
                        : `${escritorios.length} escritórios`
                      }
                    </p>
                  )}
                </div>
                <ChevronUp size={16} className="flex-shrink-0 text-[var(--color-text-secondary)]" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        {/* Recolhido o menu abre para a DIREITA: com 64px de rail, `top/start`
            deixaria o painel colado na borda esquerda da tela. */}
        <DropdownMenuContent
          side={recolhido ? 'right' : 'top'}
          align={recolhido ? 'end' : 'start'}
          className="w-[220px]"
        >
          <DropdownMenuLabel className="font-normal">
            <p className="break-words text-sm font-semibold text-[var(--color-text-primary)]">
              {user?.nome}
            </p>
            <p className="[overflow-wrap:anywhere] text-xs text-[var(--color-text-secondary)]">
              {user?.email}
            </p>
          </DropdownMenuLabel>
          {!isAdmin && planLabel && !atuacao && (
            <>
              <DropdownMenuSeparator />
              <Link
                to="/app/assinatura"
                onClick={() => onNavClick?.()}
                className="flex items-center justify-between px-2 py-1.5 rounded-sm transition-colors hover:bg-accent"
              >
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {planLabel}
                </span>
                {showUpgrade && (
                  <span className="text-xs font-medium text-[var(--color-primary)]">
                    Fazer upgrade
                  </span>
                )}
              </Link>
            </>
          )}
          <DropdownMenuSeparator />
          {/* Na conta de outra pessoa, configurações e assinatura não se aplicam. */}
          {!atuacao && (
            <DropdownMenuItem onClick={() => { navigate('/app/configuracoes'); onNavClick?.(); }}>
              <Settings size={16} />
              Configurações
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => toggleTheme()}>
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            {isDarkMode ? 'Modo claro' : 'Modo escuro'}
          </DropdownMenuItem>
          {!isAdmin && !atuacao && (
            <DropdownMenuItem onClick={() => { navigate('/app/assinatura'); onNavClick?.(); }}>
              <CreditCard size={16} />
              Assinatura
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            disabled={loggingOut}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            {loggingOut ? <Spinner className="text-inherit" /> : <LogOut size={16} />}
            {loggingOut ? 'Saindo...' : 'Sair'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

type NavSection =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; name: string; items: NavItem[] };

function buildSections(navItems: NavItem[]): NavSection[] {
  const sections: NavSection[] = [];
  for (const item of navItems) {
    if (!item.group) {
      sections.push({ kind: 'item', item });
      continue;
    }
    const last = sections[sections.length - 1];
    if (last && last.kind === 'group' && last.name === item.group) {
      last.items.push(item);
    } else {
      sections.push({ kind: 'group', name: item.group, items: [item] });
    }
  }
  return sections;
}

function isItemActive(pathname: string, itemPath: string): boolean {
  if (pathname === itemPath) return true;
  if (itemPath === '/app' || itemPath === '/app/admin') return false;
  return pathname.startsWith(itemPath);
}

const COLLAPSED_GROUPS_KEY_PREFIX = 'jurisapp:sidebar-collapsed-groups:';

function loadCollapsedGroups(userId: string | undefined): Set<string> {
  if (!userId || typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(COLLAPSED_GROUPS_KEY_PREFIX + userId);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((s) => typeof s === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function saveCollapsedGroups(userId: string | undefined, collapsed: Set<string>): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      COLLAPSED_GROUPS_KEY_PREFIX + userId,
      JSON.stringify(Array.from(collapsed))
    );
  } catch {
    // localStorage indisponível (modo privado, quota) — degrada para não-persistente
  }
}

function NavGroup({
  name,
  items,
  pathname,
  collapsed,
  forceOpen,
  recolhido,
  onToggle,
  onNavClick,
}: {
  name: string;
  items: NavItem[];
  pathname: string;
  collapsed: boolean;
  forceOpen: boolean;
  recolhido?: boolean;
  onToggle: () => void;
  onNavClick?: () => void;
}) {
  const expanded = forceOpen || !collapsed;
  const panelId = `nav-group-${name.replace(/\s+/g, '-').toLowerCase()}`;

  // Menu recolhido: sem largura para o rótulo do grupo, o recolher/expandir do
  // grupo perde a razão de existir — os itens viram uma faixa separada por uma
  // linha, e o nome do grupo aparece no tooltip de cada ícone.
  if (recolhido) {
    return (
      <div
        aria-label={name}
        className="mt-2 space-y-0.5 border-t border-[var(--color-divider)] pt-2"
      >
        {items.map((item) => (
          <NavButton
            key={item.path}
            item={item}
            isActive={isItemActive(pathname, item.path)}
            recolhido
            onClick={onNavClick}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full cursor-pointer items-center justify-between px-3 pt-3 pb-1 text-left transition-colors hover:text-[var(--color-text-primary)]"
      >
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]/60">
          {name}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            'flex-shrink-0 text-[var(--color-text-secondary)]/60 transition-transform',
            !expanded && '-rotate-90'
          )}
        />
      </button>
      {expanded && (
        <div id={panelId} className="space-y-0.5">
          {items.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              isActive={isItemActive(pathname, item.path)}
              onClick={onNavClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({
  navItems,
  headerExtra,
  footerLink,
  onNavClick,
  syncIndicator,
  recolhido,
  onToggleRecolhido,
}: SidebarLayoutProps & {
  onNavClick?: () => void;
  syncIndicator?: ReactNode;
  recolhido?: boolean;
  /** Ausente no mobile (o Sheet já abre/fecha o menu inteiro). */
  onToggleRecolhido?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';
  const userId = user?.id;

  const sections = useMemo(() => buildSections(navItems), [navItems]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => loadCollapsedGroups(userId));

  // Recarregar estado salvo quando o usuário mudar (login de outra conta no mesmo browser)
  useEffect(() => {
    setCollapsedGroups(loadCollapsedGroups(userId));
  }, [userId]);

  const activeGroup = useMemo(() => {
    for (const section of sections) {
      if (section.kind !== 'group') continue;
      if (section.items.some((item) => isItemActive(location.pathname, item.path))) {
        return section.name;
      }
    }
    return null;
  }, [sections, location.pathname]);

  const toggleGroup = (name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      saveCollapsedGroups(userId, next);
      return next;
    });
  };

  // Chevron duplo apontando para onde o menu vai: « recolhe, » expande. `Button`
  // do design system — o `cursor-pointer` e o foco visível vivem nele.
  const botaoRecolher = onToggleRecolhido && (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleRecolhido}
          aria-label={recolhido ? 'Expandir menu' : 'Recolher menu'}
          aria-expanded={!recolhido}
          className="h-8 w-8 text-[var(--color-text-secondary)] hover:bg-[var(--color-text-primary)]/5 hover:text-[var(--color-text-primary)]"
        >
          {recolhido ? <ChevronsRight className="h-[1.05rem] w-[1.05rem]" /> : <ChevronsLeft className="h-[1.05rem] w-[1.05rem]" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">{recolhido ? 'Expandir menu' : 'Recolher menu'}</TooltipContent>
    </Tooltip>
  );

  const footerLinkNode = footerLink && (
    <Link
      to={footerLink.path}
      onClick={onNavClick}
      aria-label={recolhido ? footerLink.label : undefined}
      className={cn(
        'flex items-center rounded-lg text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-text-primary)]/5 hover:text-[var(--color-text-primary)]',
        recolhido ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2',
      )}
    >
      <span className="flex-shrink-0">{footerLink.icon}</span>
      {!recolhido && footerLink.label}
    </Link>
  );

  return (
    <div className="flex h-full flex-col">
      {/* No celular o logo também fica no menu aberto: a faixa dele é onde o X do Sheet
          pousa, sem cobrir o seletor de conta logo abaixo. */}
      <div
        className={cn(
          'flex items-center',
          recolhido ? 'flex-col gap-2 px-2 py-3' : 'justify-between gap-2 px-4 py-3',
        )}
      >
        <Logo
          size="small"
          showBadge={isAdmin && !recolhido}
          badgeText="Admin"
          iconOnly={recolhido}
          onClick={() => { navigate('/app'); onNavClick?.(); }}
        />
        {botaoRecolher}
      </div>

      {/* Seletor de conta (delegação): só aparece para quem atua na conta de alguém. */}
      <div className={cn(recolhido ? 'flex justify-center px-2 pb-2' : 'px-2 pb-2')}>
        <SeletorDeConta variante={recolhido ? 'rail' : 'barra'} onNavClick={onNavClick} />
      </div>

      {headerExtra && !recolhido && (
        <div className="px-2">
          {headerExtra}
        </div>
      )}

      <Separator />

      <nav
        aria-label="Menu principal"
        className="min-h-0 flex-1 overflow-y-auto space-y-0.5 px-2 pt-3"
      >
        {sections.map((section) => {
          if (section.kind === 'item') {
            return (
              <NavButton
                key={section.item.path}
                item={section.item}
                isActive={isItemActive(location.pathname, section.item.path)}
                recolhido={recolhido}
                onClick={onNavClick}
              />
            );
          }
          return (
            <NavGroup
              key={section.name}
              name={section.name}
              items={section.items}
              pathname={location.pathname}
              collapsed={collapsedGroups.has(section.name)}
              forceOpen={activeGroup === section.name}
              recolhido={recolhido}
              onToggle={() => toggleGroup(section.name)}
              onNavClick={onNavClick}
            />
          );
        })}
      </nav>

      {footerLink && (
        <div className="px-2 pb-1">
          {recolhido ? (
            <Tooltip>
              <TooltipTrigger asChild>{footerLinkNode}</TooltipTrigger>
              <TooltipContent side="right">{footerLink.label}</TooltipContent>
            </Tooltip>
          ) : (
            footerLinkNode
          )}
        </div>
      )}

      {syncIndicator && (
        <div className={cn('py-2', recolhido ? 'flex justify-center px-2' : 'px-3')}>
          {syncIndicator}
        </div>
      )}

      <Separator />
      <UserSection recolhido={recolhido} onNavClick={onNavClick} />
    </div>
  );
}

export default function SidebarLayout({
  navItems,
  headerExtra,
  banner,
  footerLink,
  sidebarClassName,
  emPainel = false,
}: SidebarLayoutProps) {
  // O menu (Sheet) lembra EM QUE ROTA foi aberto e só está aberto nela: trocar
  // de rota o fecha. Os links dele já fecham ao clicar; o que escapava era o
  // Voltar do navegador, que deixava o menu aberto por cima da tela seguinte (no
  // desktop, em dobro com a barra lateral que reaparece ao sair dos autos).
  const { pathname } = useLocation();
  const [abertoEm, setAbertoEm] = useState<string | null>(null);
  const open = abertoEm === pathname;
  const setOpen = useCallback((aberto: boolean) => setAbertoEm(aberto ? pathname : null), [pathname]);
  // Pedido da tela montada (ver `lib/cromoApp`): os autos pedem o menu oculto.
  // Dura só enquanto a tela existe.
  const [menuOculto, setMenuOculto] = useState(false);
  // Telas de trabalho (autos, peticionar) pedem o card colado: a largura inteira.
  const [colado, setColado] = useState(false);
  // Os autos pedem também SEM RESPIRO: vão de ponta a ponta e cuidam do espaço.
  const [semRespiro, setSemRespiro] = useState(false);
  const comCard = emPainel && !colado;
  // Rail de ícones: preferência persistida por usuário. Vale só no DESKTOP — no
  // mobile o menu já é um Sheet que some inteiro quando fechado.
  const [recolhido, setRecolhido] = useSidebarRecolhido();
  const syncStatus = useSyncStatus();

  // Com o menu oculto, o caminho de volta a ele é o MESMO Sheet do celular: o
  // conteúdo é portalado, então abre por cima também no desktop, embora o
  // cabeçalho que o hospeda seja `sm:hidden`.
  //
  // Quem abriu fica guardado para receber o foco de volta: o Radix o devolveria
  // ao gatilho do Sheet, que no desktop está escondido — e o foco caía no
  // `body`, com o próximo Tab recomeçando do topo.
  const abriuOMenu = useRef<HTMLElement | null>(null);
  const cromo = useMemo<CromoApp>(
    () => ({
      pedirOcultacao: setMenuOculto,
      pedirColagem: setColado,
      pedirSemRespiro: setSemRespiro,
      abrirMenu: menuOculto
        ? () => {
            abriuOMenu.current = document.activeElement as HTMLElement | null;
            setOpen(true);
          }
        : undefined,
    }),
    [menuOculto, setOpen],
  );
  const devolverFoco = (e: Event) => {
    const quem = abriuOMenu.current;
    abriuOMenu.current = null;
    if (quem?.isConnected && quem.getClientRects().length > 0) {
      e.preventDefault();
      quem.focus();
    }
  };


  return (
    <CromoAppContext.Provider value={cromo}>
    <div
      className={cn(
        'flex h-[calc(100dvh-var(--env-banner-h,0px))] overflow-hidden',
        comCard ? 'bg-[var(--color-bg-dark)]' : 'bg-[var(--color-bg-default)]',
        // No celular, quanto o topo do app tira da tela: a barra de 56px e o respiro de
        // 16px, ou só 8px quando a tela escondeu o menu (os autos). As telas de altura
        // fixa descontam isto.
        menuOculto ? '[--topo-app:0.5rem]' : '[--topo-app:4.5rem]',
      )}
      data-moldura={comCard ? 'card' : undefined}
    >
      {/* Mobile: barra de instalação do PWA (fixa no topo, abaixo do ambiente) */}
      <InstallBar />
      {/* Mobile: header fixo + Sheet. Com o menu oculto (autos), a barra sai também
          (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.6): o ☰ vai para a linha do processo
          (`BotaoMenuDoApp`). Escondida, não desmontada — o Sheet do menu mora aqui. */}
      <header className={cn("fixed inset-x-0 top-[calc(var(--env-banner-h,0px)+var(--install-bar-h,0px))] z-50 flex h-14 items-center border-b border-[var(--color-divider)] bg-[var(--color-bg-default)] px-4 sm:hidden", menuOculto && 'hidden', sidebarClassName)}>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Abrir menu de navegação" className="-ml-2 h-11 w-11 text-[var(--color-text-primary)]">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className={cn("flex w-[248px] flex-col p-0", sidebarClassName)}
            onCloseAutoFocus={devolverFoco}
          >
            <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
            <SheetDescription className="sr-only">Navegação principal do aplicativo</SheetDescription>
            <SidebarContent
              navItems={navItems}
              headerExtra={headerExtra}
              footerLink={footerLink}
              onNavClick={() => setOpen(false)}
              syncIndicator={<SyncIndicator pendingCount={syncStatus.pendingCount} failedCount={syncStatus.failedCount} isSyncing={syncStatus.isSyncing} />}
            />
          </SheetContent>
        </Sheet>
        <div className="flex flex-1 justify-center">
          <Logo size="small" />
        </div>
        <div className="mr-2">
          <SeletorDeConta variante="chip" />
        </div>
        <div className="flex w-6 items-center justify-end">
          <SyncIndicator pendingCount={syncStatus.pendingCount} failedCount={syncStatus.failedCount} isSyncing={syncStatus.isSyncing} />
        </div>
      </header>

      {/* Desktop: aside fixa — recolhe para um rail de 64px só com os ícones.
          Some por inteiro nos autos (`useMenuOculto`). */}
      {!menuOculto && (
      <aside
        className={cn(
          'hidden h-full flex-shrink-0 flex-col border-r border-[var(--color-divider)] bg-[var(--color-bg-default)] transition-[width] duration-200 sm:flex',
          recolhido ? 'w-16' : 'w-[248px]',
          sidebarClassName,
        )}
      >
        <SidebarContent
          navItems={navItems}
          headerExtra={headerExtra}
          footerLink={footerLink}
          syncIndicator={<SyncIndicator pendingCount={syncStatus.pendingCount} failedCount={syncStatus.failedCount} isSyncing={syncStatus.isSyncing} />}
          recolhido={recolhido}
          onToggleRecolhido={() => setRecolhido(!recolhido)}
        />
      </aside>
      )}

      {/* Main content */}
      <FabProvider>
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            'min-w-0 flex-1 overflow-y-auto overscroll-contain bg-[var(--color-bg-dark)] p-4 pt-[calc(var(--topo-app)+var(--install-bar-h,0px))] outline-none sm:p-6 sm:pt-6',
            // A moldura: o card sobre o fundo, com o vão de 14px do menu e do topo e
            // 16px à direita e embaixo. Abaixo de 640px não há card.
            comCard &&
              'fs-painel sm:mb-4 sm:ml-3.5 sm:mr-4 sm:mt-3.5 sm:rounded-[var(--radius-xl)] sm:border sm:border-[var(--color-border)] sm:bg-[var(--color-bg-paper)]',
            // Colado (telas de trabalho): menos respiro, a área vai para o documento.
            // As telas de altura fixa descontam estes 12px em cima e embaixo.
            colado && 'sm:px-4 sm:py-3',
            semRespiro && 'sm:p-0',
          )}
        >
          {/* Sem o respiro do contêiner (autos), a faixa do topo (certificado vencendo)
              ganha o dela: colada nas bordas, o anel e os cantos saíam cortados. Só
              quando há faixa: o `banner` é um elemento mesmo quando nada aparece, e o
              respiro vazio empurrava a tela 8px para baixo (margem no topo e a página
              rolando). `empty:hidden` some com o invólucro sem conteúdo. */}
          {banner && <div className={cn(semRespiro && 'sm:px-2 sm:pt-2', 'empty:hidden')}>{banner}</div>}
          <SubscriptionGate>
            <Outlet />
          </SubscriptionGate>
        </main>
        <FabRenderer />
      </FabProvider>
    </div>
    </CromoAppContext.Provider>
  );
}
